import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Save, Loader2, ChevronUp, ChevronDown, Settings, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { FIELD_TYPES } from "@/lib/gastos";

const slugify = (s) =>
  (s || "").toString().trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "";

const emptyCampo = () => ({
  key: "", label: "", tipo: "texto", obligatorio: false,
  placeholder: "", ayuda: "", opciones: [], config: {},
});

const emptyCategoria = () => ({
  nombre: "", descripcion: "", icono: "", estado: "activo", orden: 0,
  constantes: [], campo_monto: "", campos: [], limite_monto: 0,
});

export default function GastosConfig() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // categoria en edición o null
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/gastos/categorias");
      setCategorias(r.data || []);
    } catch { toast.error("Error al cargar categorías"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const seed = async () => {
    try { const r = await api.post("/api/gastos/categorias/seed"); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const startNew = () => setEditing(emptyCategoria());
  const startEdit = (c) => setEditing(JSON.parse(JSON.stringify(c)));

  const removeCategoria = async (c) => {
    if (!window.confirm(`¿Eliminar la categoría ${c.nombre}?`)) return;
    try {
      const r = await api.delete(`/api/gastos/categorias/${c.id}`);
      toast.success(r.data.desactivada ? "Categoría desactivada (estaba en uso)" : "Categoría eliminada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  // ---- edición ----
  const upd = (patch) => setEditing((p) => ({ ...p, ...patch }));

  const setCampo = (i, patch) =>
    setEditing((p) => ({ ...p, campos: p.campos.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const setCampoConfig = (i, patch) =>
    setEditing((p) => ({ ...p, campos: p.campos.map((c, idx) => (idx === i ? { ...c, config: { ...c.config, ...patch } } : c)) }));
  const addCampo = () => setEditing((p) => ({ ...p, campos: [...p.campos, emptyCampo()] }));
  const removeCampo = (i) => setEditing((p) => ({ ...p, campos: p.campos.filter((_, idx) => idx !== i) }));
  const moveCampo = (i, dir) => setEditing((p) => {
    const arr = [...p.campos]; const j = i + dir;
    if (j < 0 || j >= arr.length) return p;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...p, campos: arr };
  });

  const addConst = () => setEditing((p) => ({ ...p, constantes: [...(p.constantes || []), { key: "", label: "", valor: 0 }] }));
  const setConst = (i, patch) =>
    setEditing((p) => ({ ...p, constantes: p.constantes.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const removeConst = (i) => setEditing((p) => ({ ...p, constantes: p.constantes.filter((_, idx) => idx !== i) }));

  const direccionCampos = (editing?.campos || []).filter((c) => c.tipo === "direccion");
  const montoCandidatos = (editing?.campos || []).filter((c) => ["monto", "calculo", "numero"].includes(c.tipo));

  const guardar = async () => {
    if (!editing.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    // Normalizar keys y orden, registrando el remapeo key-original -> key-final
    // para reescribir referencias (distancia origen/destino, fórmulas).
    const usados = new Set();
    const keyMap = {};
    const campos = editing.campos.map((c, idx) => {
      const oldKey = c.key?.trim() || slugify(c.label) || `campo_${idx + 1}`;
      let key = oldKey;
      while (usados.has(key)) key = `${key}_${idx + 1}`;
      usados.add(key);
      if (!(oldKey in keyMap)) keyMap[oldKey] = key; // primera ocurrencia gana
      return { ...c, key, orden: idx + 1 };
    });
    campos.forEach((c) => {
      if (c.tipo === "distancia") {
        const cfg = { ...(c.config || {}) };
        if (cfg.origen) cfg.origen = keyMap[cfg.origen] || cfg.origen;
        if (cfg.destino) cfg.destino = keyMap[cfg.destino] || cfg.destino;
        c.config = cfg;
      }
      if (c.tipo === "calculo" && (c.config || {}).formula) {
        c.config = {
          ...c.config,
          formula: c.config.formula.replace(/\{([^}]+)\}/g, (m, k) => {
            const t = k.trim();
            if (t.startsWith("const:")) return m;
            return `{${keyMap[t] || t}}`;
          }),
        };
      }
    });
    const body = { ...editing, campos };
    setSaving(true);
    try {
      if (editing.id) await api.put(`/api/gastos/categorias/${editing.id}`, body);
      else await api.post("/api/gastos/categorias", body);
      toast.success("Categoría guardada");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  // =================== LISTA ===================
  if (!editing) {
    return (
      <div className="min-h-screen bg-[#F5F7FA]">
        <header className="bg-white border-b sticky top-0 z-30">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <Settings className="h-5 w-5 text-[#534AB7] flex-shrink-0" />
              <span className="text-sm font-semibold text-[#384091] truncate">Configuración · Rendición de Gastos</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" className="bg-[#534AB7] hover:bg-[#443C9E] text-white" onClick={startNew}>
                <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Nueva categoría</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/gastos")}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-4 space-y-3">
          <p className="text-sm text-gray-500">
            Define las categorías de gasto y los campos que se llenan en cada una. Los campos de tipo
            <b> distancia</b> calculan km entre dos direcciones y los de tipo <b>cálculo</b> aplican una fórmula
            (ej. km × costo por km).
          </p>

          {loading ? (
            <p className="text-center text-gray-400 py-12">Cargando...</p>
          ) : categorias.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-8 text-center space-y-3">
                <p className="text-gray-500">Aún no hay categorías configuradas.</p>
                <Button variant="outline" className="border-[#534AB7] text-[#534AB7]" onClick={seed}>
                  <Wand2 className="h-4 w-4 mr-1" /> Cargar categorías de ejemplo (Peajes, Combustible)
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {categorias.map((c) => (
                <Card key={c.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <button className="flex-1 min-w-0 text-left" onClick={() => startEdit(c)}>
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {c.nombre}
                        <Badge className={`ml-2 border-0 ${c.estado === "activo" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                          {c.estado}
                        </Badge>
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {(c.campos || []).length} campo(s){c.descripcion ? ` · ${c.descripcion}` : ""}
                      </p>
                    </button>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(c)}>Editar</Button>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600" onClick={() => removeCategoria(c)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  // =================== EDITOR CATEGORÍA ===================
  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-[#384091] truncate">
            {editing.id ? `Editar: ${editing.nombre}` : "Nueva categoría"}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" className="bg-[#534AB7] hover:bg-[#443C9E] text-white" onClick={guardar} disabled={saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Datos categoría */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <h2 className="font-bold text-lg text-[#384091]">Categoría</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nombre <span className="text-red-500 text-xs">*</span></Label>
                <Input value={editing.nombre} onChange={(e) => upd({ nombre: e.target.value })}
                  placeholder="Ej: Peajes" className="mt-1 h-11 bg-white" /></div>
              <div><Label>Estado</Label>
                <Select value={editing.estado} onValueChange={(v) => upd({ estado: v })}>
                  <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="activo">Activo</SelectItem>
                    <SelectItem value="inactivo">Inactivo</SelectItem>
                  </SelectContent>
                </Select></div>
              <div className="sm:col-span-2"><Label>Descripción</Label>
                <Input value={editing.descripcion} onChange={(e) => upd({ descripcion: e.target.value })}
                  placeholder="Descripción breve" className="mt-1 h-11 bg-white" /></div>
              <div className="sm:col-span-2"><Label>Límite por gasto (CLP) — política</Label>
                <Input type="number" value={editing.limite_monto || ""} onChange={(e) => upd({ limite_monto: e.target.value })}
                  placeholder="0 = sin límite" className="mt-1 h-11 bg-white no-uppercase" />
                <p className="text-[11px] text-gray-400 mt-1">Los gastos que superen este monto se marcan con alerta al enviar la rendición (no se bloquean).</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Constantes */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-lg text-[#384091]">Constantes</h2>
                <p className="text-xs text-gray-500">Valores fijos usados por las fórmulas (ej. costo_por_km = 250).</p>
              </div>
              <Button variant="outline" size="sm" onClick={addConst}><Plus className="h-3.5 w-3.5 mr-1" /> Agregar</Button>
            </div>
            {(editing.constantes || []).length === 0 && <p className="text-xs text-gray-400">Sin constantes.</p>}
            {(editing.constantes || []).map((k, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,1fr,auto] gap-2 items-end">
                <div><Label className="text-xs">Clave</Label>
                  <Input value={k.key} onChange={(e) => setConst(i, { key: slugify(e.target.value) })}
                    placeholder="costo_por_km" className="h-10 bg-white no-uppercase" /></div>
                <div><Label className="text-xs">Etiqueta</Label>
                  <Input value={k.label} onChange={(e) => setConst(i, { label: e.target.value })}
                    placeholder="Costo por km" className="h-10 bg-white" /></div>
                <div><Label className="text-xs">Valor</Label>
                  <Input type="number" value={k.valor} onChange={(e) => setConst(i, { valor: e.target.value })}
                    className="h-10 bg-white no-uppercase" /></div>
                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 h-10 w-10 p-0"
                  onClick={() => removeConst(i)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Campos */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-lg text-[#384091]">Campos del formulario</h2>
              <Button variant="outline" size="sm" onClick={addCampo}><Plus className="h-3.5 w-3.5 mr-1" /> Agregar campo</Button>
            </div>
            {editing.campos.length === 0 && <p className="text-xs text-gray-400">Sin campos aún.</p>}

            {editing.campos.map((campo, i) => (
              <div key={i} className="rounded-lg border border-gray-200 p-3 space-y-3 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">Campo {i + 1}</span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => moveCampo(i, -1)}><ChevronUp className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => moveCampo(i, 1)}><ChevronDown className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600" onClick={() => removeCampo(i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div><Label className="text-xs">Etiqueta</Label>
                    <Input value={campo.label} onChange={(e) => setCampo(i, { label: e.target.value })}
                      placeholder="Ej: Punto de origen" className="h-10 bg-white" /></div>
                  <div><Label className="text-xs">Tipo</Label>
                    <Select value={campo.tipo} onValueChange={(v) => setCampo(i, { tipo: v })}>
                      <SelectTrigger className="h-10 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Clave (opcional)</Label>
                    <Input value={campo.key} onChange={(e) => setCampo(i, { key: slugify(e.target.value) })}
                      placeholder="auto desde etiqueta" className="h-10 bg-white no-uppercase" /></div>
                  <div className="flex items-center gap-2 pt-6">
                    <Checkbox checked={!!campo.obligatorio} onCheckedChange={(v) => setCampo(i, { obligatorio: !!v })} id={`req-${i}`} />
                    <Label htmlFor={`req-${i}`} className="text-sm cursor-pointer">Obligatorio</Label>
                  </div>
                </div>

                {/* Config específica por tipo */}
                {campo.tipo === "select" && (
                  <div>
                    <Label className="text-xs">Opciones (una por línea)</Label>
                    <Textarea rows={3} className="bg-white no-uppercase"
                      value={(campo.opciones || []).map((o) => (typeof o === "string" ? o : o.label || o.value)).join("\n")}
                      onChange={(e) => setCampo(i, { opciones: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                      placeholder={"Opción A\nOpción B"} />
                  </div>
                )}

                {campo.tipo === "distancia" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div><Label className="text-xs">Campo origen (dirección)</Label>
                      <Select value={campo.config?.origen || ""} onValueChange={(v) => setCampoConfig(i, { origen: v })}>
                        <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {direccionCampos.map((d, di) => <SelectItem key={di} value={d.key || slugify(d.label)}>{d.label || d.key}</SelectItem>)}
                        </SelectContent>
                      </Select></div>
                    <div><Label className="text-xs">Campo destino (dirección)</Label>
                      <Select value={campo.config?.destino || ""} onValueChange={(v) => setCampoConfig(i, { destino: v })}>
                        <SelectTrigger className="h-10 bg-white"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {direccionCampos.map((d, di) => <SelectItem key={di} value={d.key || slugify(d.label)}>{d.label || d.key}</SelectItem>)}
                        </SelectContent>
                      </Select></div>
                    <p className="sm:col-span-2 text-[11px] text-gray-400">
                      Requiere dos campos de tipo Dirección. Si no aparecen, créalos primero y asígnales una clave.
                    </p>
                  </div>
                )}

                {campo.tipo === "calculo" && (
                  <div className="space-y-2">
                    <div><Label className="text-xs">Fórmula</Label>
                      <Input value={campo.config?.formula || ""} onChange={(e) => setCampoConfig(i, { formula: e.target.value })}
                        placeholder="{distancia} * {const:costo_por_km}" className="h-10 bg-white no-uppercase" /></div>
                    <p className="text-[11px] text-gray-400">
                      Variables disponibles: {(editing.campos || []).filter((c) => c.key || c.label).map((c) => `{${c.key || slugify(c.label)}}`).join(" ")}
                      {(editing.constantes || []).length ? " · " + editing.constantes.map((k) => `{const:${k.key}}`).join(" ") : ""}
                    </p>
                  </div>
                )}

                {campo.tipo === "imagen" && (
                  <div className="flex items-center gap-2">
                    <Checkbox checked={!!campo.config?.multiple} onCheckedChange={(v) => setCampoConfig(i, { multiple: !!v })} id={`mult-${i}`} />
                    <Label htmlFor={`mult-${i}`} className="text-sm cursor-pointer">Permitir múltiples imágenes</Label>
                  </div>
                )}

                {["texto", "textarea", "numero", "monto", "direccion"].includes(campo.tipo) && (
                  <div><Label className="text-xs">Placeholder / ayuda (opcional)</Label>
                    <Input value={campo.placeholder} onChange={(e) => setCampo(i, { placeholder: e.target.value })}
                      placeholder="Texto de ayuda dentro del campo" className="h-10 bg-white" /></div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Campo monto */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-2">
            <h2 className="font-bold text-lg text-[#384091]">Monto del gasto</h2>
            <p className="text-xs text-gray-500">¿Qué campo representa el monto (CLP) que se suma al total de la rendición?</p>
            <Select value={editing.campo_monto || ""} onValueChange={(v) => upd({ campo_monto: v })}>
              <SelectTrigger className="h-11 bg-white max-w-sm"><SelectValue placeholder="Seleccionar campo de monto" /></SelectTrigger>
              <SelectContent className="bg-white">
                {montoCandidatos.map((c, i) => <SelectItem key={i} value={c.key || slugify(c.label)}>{c.label || c.key}</SelectItem>)}
              </SelectContent>
            </Select>
            {montoCandidatos.length === 0 && (
              <p className="text-[11px] text-amber-600">Crea un campo de tipo Monto o Cálculo para poder elegir el monto.</p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
